import { useState, useEffect, useMemo, useRef } from "react";
import { Box, Divider } from "@mui/material";
import { useUser } from "../../../Context/UserContext";

import trackinghoodsheader from "../../../Config/Export/CreateNewJob/Tracking/Trackinggoodsheader";
import trackinggoodsrecipt from "../../../Config/Export/CreateNewJob/Tracking/Trackingsgoodsrecipt";

import FormGenerator from "../../../Utils/FormGenerator/FormGenerator";
import TableGenerator from "../../../Utils/FormGenerator/TableGenerator";
import FormDialog from "@/Components/Dialog/FormDialog";

// ✅ RTK API HOOKS
import {
  useCreateExportTrackingMutation,
  useUpdateExportTrackingMutation,
  useDeleteExportTrackingMutation,
  useLazyGetGoodsReceiptByExportMasterIdQuery,
} from "../../../redux/ExportPage/CreateNewJob/Tracking/GoodsReciptApi";
import { OutLinedButton } from "../../../Components/Buttons/OutLinedButton";
import SuccessDialog from "../../../Components/Dialog/SuccessDialog";
import { LuCheck } from "react-icons/lu";

export default function TrackingGoodsRecipt({ onClose }) {
  const headerFormRef = useRef();   // ✅ for HEADER FormGenerator
  const formRef = useRef();         // ✅ for ADD / EDIT dialog
  
  const { formData, setFormData } = useUser();

  const exportMasterId = formData?.general?.exporterMasterId;

  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [prefillData, setPrefillData] = useState({});
  const loading = useUser((s) => s.loading);
  const setLoading = useUser((s) => s.setLoading);
  const [successDialogConfig, setSuccessDialogConfig] = useState(null);
  const [subDialogConfig, setSubDialogConfig] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  // ✅ API HOOKS
  const [createExportTracking] = useCreateExportTrackingMutation();
  const [updateExportTracking] = useUpdateExportTrackingMutation();
  const [deleteExportTracking] = useDeleteExportTrackingMutation();
  const [fetchGoodsReceipt] = useLazyGetGoodsReceiptByExportMasterIdQuery();

  // ✅ TABLE COLUMNS
  const visibleFields = useMemo(
    () => [
      { key: "goodsReceiptReceivedQty", label: "Received Qty" },
      { key: "goodsReceiptDate", label: "Received Date" },
      { key: "goodsReceiptTransporter", label: "Transporter" },
      { key: "goodsReceiptVehicleNo", label: "Vehicle No." },
    ],
    []
  );

  // ✅ ✅ ✅ LOAD FROM API → SET TABLE + GLOBAL FORMDATA
  useEffect(() => {
    if (!exportMasterId) return;
  
    const loadData = async () => {
      try {
        const res = await fetchGoodsReceipt({ exportMasterId }).unwrap();
        const data = res || [];
  
        console.log("✅ GOODS RECEIPT FROM API:", data);
  
        const formatted = data.map((item, index) => ({
          ...item,
  
          // ✅ ONLY FIRST ROW SELECTED
          selected: index === 0,
  
          goodsReceiptTransporter:
            typeof item.goodsReceiptTransporter === "object"
              ? item.goodsReceiptTransporter?.name ?? "-"
              : item.goodsReceiptTransporter,
  
          goodsReceiptTransporterId:
            typeof item.goodsReceiptTransporter === "object"
              ? item.goodsReceiptTransporter?.id
              : item.goodsReceiptTransporterId,
  
          goodsReceiptDate: item.goodsReceiptDate ?? "-",
        }));
  
        setRows(formatted);
  
        setFormData((prev) => ({
          ...prev,
          goodsReceipt: formatted,
        }));
      } catch (err) {
        console.error("❌ GOODS RECEIPT FETCH FAILED:", err);
      }
    };
  
    loadData();
  }, [exportMasterId]);
const handleRowClick = (row) => {
  // ✅ Update LOCAL rows
  setRows((prev) =>
    prev.map((r) => ({
      ...r,
      selected: r.goodsReceiptId === row.goodsReceiptId,
    }))
  );

  // ✅ Update GLOBAL formData (TableGenerator இதை read பண்ணுது)
  setFormData((prev) => ({
    ...prev,
    goodsReceipt: prev.goodsReceipt.map((r) => ({
      ...r,
      selected: r.goodsReceiptId === row.goodsReceiptId,
    })),
  }));
};

  // ✅ ✅ EDIT CLICK → OPEN DIALOG + PREFILL
  const handleEditRow = (row) => {
    const prefill = {
      ...row,
      goodsReceiptTransporter: row.goodsReceiptTransporterId
        ? {
            id: row.goodsReceiptTransporterId,
            name: row.goodsReceiptTransporter,
          }
        : null,
    };
    console.log("✅ EDIT PREFILL:", prefill);
  
    setIsEditing(true);
    setEditingRow(row);
    setPrefillData(prefill);
    setShowForm(true);
  };
  

  // ✅ ✅ DELETE
  const handleDeleteRow = async (row) => {
    const okConfirm = window.confirm(
      "Are you sure you want to delete this Goods Receipt?"
    );
    if (!okConfirm) return;
  
    try {
      setLoading(true);
  
      const response = await deleteExportTracking({
        goodsReceiptId: row.goodsReceiptId,
      }).unwrap();
  
      // ✅ ✅ IMMEDIATE UI UPDATE
      setRows((prev) =>
        prev.filter((r) => r.goodsReceiptId !== row.goodsReceiptId)
      );
  
      setFormData((prev) => ({
        ...prev,
        goodsReceipt: prev.goodsReceipt.filter(
          (r) => r.goodsReceiptId !== row.goodsReceiptId
        ),
      }));
  
      // ✅ ✅ SUCCESS CONDITION
      const ok =
        response?.data?.statusCode === 200 || response?.statusCode === 200;
  
      // ✅ ✅ SUCCESS / FAILURE DIALOG
      setSuccessDialogConfig({
        label: ok ? "Success" : "Failure",
        title: "",
        subtitle: (
          <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
            {response?.data?.message ||
              response?.message ||
              (ok ? "Data deleted successfully" : "Something went wrong")}
          </strong>
        ),
        buttonText: "OK",
        IconComponent: ok ? LuCheck : BiSolidError,
        status: ok ? "success" : "error",
      });
  
      setShowSuccessDialog(true);
  
      return response;
    } catch (err) {
      console.error("❌ ERROR in handleDeleteRow():", err);
  
      // ✅ ✅ FAILURE DIALOG
      setSuccessDialogConfig({
        label: "Failure",
        title: "",
        subtitle: (
          <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
            {err?.data?.message || "Something went wrong"}
          </strong>
        ),
        buttonText: "OK",
        status: "error",
        IconComponent: BiSolidError,
      });
  
      setShowSuccessDialog(true);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  

  // ✅ ✅ SAVE HANDLER (CREATE / UPDATE)
  const handleSave = async () => {
    const formValues = await formRef.current?.submit?.();
    if (!formValues) return;
  
    setSaveLoading(true);
    setLoading(true);
  
    const payload = {
      goodsReceipt: {
        goodsReceiptDate: formValues.goodsReceiptDate,
        goodsReceiptReceivedQty: formValues.goodsReceiptReceivedQty,
        goodsReceiptTransporter:
          typeof formValues.goodsReceiptTransporter === "object"
            ? formValues.goodsReceiptTransporter.id
            : formValues.goodsReceiptTransporter,
        goodsReceiptVehicleNo: formValues.goodsReceiptVehicleNo,
      },
    };
  
    let response, tableResponse;
  
    try {
      if (isEditing && editingRow?.goodsReceiptId) {
        // ✅ UPDATE
        response = await updateExportTracking({
          exportMasterId,
          goodsReceiptId: editingRow.goodsReceiptId,
          ...payload,
        }).unwrap();
      } else {
        // ✅ CREATE
        response = await createExportTracking({
          exportMasterId,
          ...payload,
        }).unwrap();
      }
  
      // ✅ ✅ ✅ AFTER SAVE → FORCE GET API REFETCH
      tableResponse = await fetchGoodsReceipt({ exportMasterId }).unwrap();
  
      const formatted = tableResponse.map((item, index) => ({
        ...item,
        selected: index === 0,
        goodsReceiptTransporter:
          typeof item.goodsReceiptTransporter === "object"
            ? item.goodsReceiptTransporter?.name ?? "-"
            : item.goodsReceiptTransporter,
        goodsReceiptTransporterId:
          typeof item.goodsReceiptTransporter === "object"
            ? item.goodsReceiptTransporter?.id
            : item.goodsReceiptTransporterId,
        goodsReceiptDate: item.goodsReceiptDate ?? "-",
      }));
  
      setRows(formatted);
  
      setFormData((prev) => ({
        ...prev,
        goodsReceipt: formatted,
      }));
  
      // ✅ ✅ ✅ SUCCESS / FAILURE STATUS CHECK
      const success =
        response?.success === true ||
        response?.data?.success === true ||
        response?.data?.statusCode === 200 ||
        response?.statusCode === 200 ||
        tableResponse?.statusCode === 200;
  
      setSuccessDialogConfig({
        label: success ? "Success" : "Failure",
        title: "",
        subtitle: (
          <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
            {response?.data?.message ||
              response?.message ||
              "Data saved successfully"}
          </strong>
        ),
        buttonText: "OK",
        IconComponent: success ? LuCheck : BiSolidError,
        status: success ? "success" : "error",
      });
  
      setShowSuccessDialog(true);
  
      // ✅ CLOSE FORM
      setShowForm(false);
      setIsEditing(false);
      setEditingRow(null);
      setPrefillData({});
  
      return { response, tableResponse };
    } catch (err) {
      console.error("❌ Save failed:", err);
  
      setSuccessDialogConfig({
        label: "Failure",
        title: "",
        subtitle: (
          <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
            {err?.data?.message || "Something went wrong"}
          </strong>
        ),
        buttonText: "OK",
        status: "error",
        icon: BiSolidError,
      });
  
      setShowSuccessDialog(true);
      throw err;
    } finally {
      // setTrigger((prev) => prev + 1);
      setLoading(false);
      setSaveLoading(false);
      setExtraApi(null);
    }
  };
  
  

  // ✅ ✅ CLOSE FORM
  const handleCloseForm = () => {
    setShowForm(false);
    setIsEditing(false);
    setEditingRow(null);
    setPrefillData({});
  };
  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", width: "100%", gap: 2 }}
    >
      {/* ✅ HEADER FORM */}
      <Box>
      <FormGenerator
  schema={trackinghoodsheader}
  initialData={formData?.tracking?.[0]?.goodsReceipt || {}}
  ref={headerFormRef}   // ✅ VERY IMPORTANT
/>

      </Box>

      <Divider />

      <OutLinedButton
      label="Add"
      onClick={() => setShowForm(true)}
      sx={{border:"none", alignSelf:"flex-end"}}
      />
      {/* ✅ TABLE */}
      <Box>
        <TableGenerator
          compKey="goodsReceipt"
          label="Goods Receipt"
          visibleFields={visibleFields}
          loading={false}
          handlers={{
            handleEdit: handleEditRow,
            handleRemove: handleDeleteRow,
            handleRowClick: handleRowClick
          }}
        />
      </Box>

      {/* ✅ ✅ FORM DIALOG */}
      {showForm && (
        <FormDialog
          title={isEditing ? "Edit Goods Receipt" : "Add Goods Receipt"}
          schema={trackinggoodsrecipt}
          isEditing={isEditing}
          onSubmit={handleSave}
          onClose={handleCloseForm}
          initialData={prefillData} // ✅ FINAL PREFILL
          showCancelButton={true}
          showSubmitButton={true}
          cancelLabel="Cancel"
          submitLabel={isEditing ? "Save" : "Add"}
          loading={loading}
          ref={formRef}
        />
      )}
            <SuccessDialog
        open={showSuccessDialog}
        onClose={() => {
          setShowSuccessDialog(false);
        }}
        {...successDialogConfig}
      />
    </Box>
  );
}
